import React from "react";
import { Typography } from "@mui/material";

import LegalPage from "./LegalPage";
import LegalSection from "./LegalSection";
import {
  CONTROLLER_ADDRESS,
  CONTROLLER_NAME,
  CONTROLLER_REGISTRATION,
  DPO_CONTACT,
  HOSTING_PROVIDER,
  PLATFORM_NAME,
  PRIVACY_CONTACT_EMAIL,
  PROCESSORS,
  RETENTION_PERIOD,
  SUPERVISORY_AUTHORITY,
} from "./legalContentConstants";

const SECTIONS = [
  { id: "controller", title: "1. Who is responsible" },
  { id: "data-we-collect", title: "2. What data we collect" },
  { id: "purposes", title: "3. Why we process it" },
  { id: "sharing", title: "4. Sharing and processors" },
  { id: "transfers", title: "5. International transfers" },
  { id: "retention", title: "6. How long we keep it" },
  { id: "rights", title: "7. Your rights" },
  { id: "cookies", title: "8. Cookies and storage" },
  { id: "security", title: "9. Security" },
  { id: "changes", title: "10. Changes to this notice" },
  { id: "contact", title: "11. Contact and complaints" },
];

const PrivacyNotice = () => {
  return (
    <LegalPage
      title="Privacy Notice"
      intro={`This notice explains how ${CONTROLLER_NAME} processes personal data when you use the ${PLATFORM_NAME}, in line with the General Data Protection Regulation (GDPR).`}
      sections={SECTIONS}
    >
      <LegalSection id="controller" title="1. Who is responsible">
        <Typography variant="body1">
          The data controller is {CONTROLLER_NAME}, {CONTROLLER_ADDRESS}
          {CONTROLLER_REGISTRATION && ` (${CONTROLLER_REGISTRATION})`}.
        </Typography>
        <Typography variant="body1">Data Protection Officer: {DPO_CONTACT}.</Typography>
      </LegalSection>

      <LegalSection id="data-we-collect" title="2. What data we collect">
        <Typography variant="body1">
          Depending on how you use the Service, we may process:
        </Typography>
        <Typography component="ul" variant="body1" sx={{ pl: 3 }}>
          <li>
            <strong>Account data:</strong> first and last name, email address,
            country, city, phone number, organisation and role.
          </li>
          <li>
            <strong>Authentication data:</strong> credentials and session
            information managed by our identity provider (Keycloak).
          </li>
          <li>
            <strong>Project content:</strong> information you enter about
            projects, including stakeholder records (names, contact details,
            demographics) and uploaded files.
          </li>
          <li>
            <strong>Assistant/chat content:</strong> messages and documents you
            submit to the wizard or chat features.
          </li>
          <li>
            <strong>Technical data:</strong> limited data needed to operate and
            secure the Service.
          </li>
        </Typography>
        <Typography variant="body1">
          TODO(legal): confirm this list matches the deployed data model and
          remove anything not actually collected.
        </Typography>
      </LegalSection>

      <LegalSection id="purposes" title="3. Why we process it">
        <Typography variant="body1">
          We process personal data to provide and secure the Service, to manage
          accounts and access, and to support the rural-development activities
          the platform is built for.
        </Typography>
        <Typography variant="body1">
          TODO(legal): state the lawful basis (e.g. consent, contract,
          legitimate interests) for each purpose, and any special-category data
          handling (e.g. stakeholder demographics).
        </Typography>
      </LegalSection>

      <LegalSection id="sharing" title="4. Sharing and processors">
        <Typography variant="body1">
          We share personal data only with service providers that process it on
          our behalf, under contract. These currently include:
        </Typography>
        <Typography component="ul" variant="body1" sx={{ pl: 3 }}>
          {PROCESSORS.map((processor) => (
            <li key={processor}>{processor}</li>
          ))}
        </Typography>
        <Typography variant="body1">
          TODO(legal): confirm the processor list and ensure data-processing
          agreements are in place.
        </Typography>
      </LegalSection>

      <LegalSection id="transfers" title="5. International transfers">
        <Typography variant="body1">
          Some providers may process data outside the European Economic Area. We
          rely on appropriate safeguards (such as Standard Contractual Clauses)
          for such transfers.
        </Typography>
        <Typography variant="body1">
          TODO(legal): identify which providers transfer data outside the EEA
          and the safeguard relied upon.
        </Typography>
      </LegalSection>

      <LegalSection id="retention" title="6. How long we keep it">
        <Typography variant="body1">{RETENTION_PERIOD}</Typography>
      </LegalSection>

      <LegalSection id="rights" title="7. Your rights">
        <Typography variant="body1">
          Under the GDPR you have the right to access, rectify, erase, restrict
          or object to the processing of your data, and the right to data
          portability. You can exercise these rights by contacting us.
        </Typography>
        <Typography variant="body1">
          TODO(legal): confirm how each right is fulfilled, including account
          deletion and data export (not yet self-service in the platform).
        </Typography>
      </LegalSection>

      <LegalSection id="cookies" title="8. Cookies and storage">
        <Typography variant="body1">
          We use cookies and browser storage to keep you signed in and to
          remember your preferences. See our Cookie &amp; Storage Policy for
          details.
        </Typography>
      </LegalSection>

      <LegalSection id="security" title="9. Security">
        <Typography variant="body1">
          We apply appropriate technical and organisational measures to protect
          personal data. Hosting is provided by {HOSTING_PROVIDER}.
        </Typography>
      </LegalSection>

      <LegalSection id="changes" title="10. Changes to this notice">
        <Typography variant="body1">
          We may update this notice. The "Last updated" date at the top shows
          the current version.
        </Typography>
      </LegalSection>

      <LegalSection id="contact" title="11. Contact and complaints">
        <Typography variant="body1">
          Contact us at {PRIVACY_CONTACT_EMAIL}. You also have the right to
          lodge a complaint with a supervisory authority: {SUPERVISORY_AUTHORITY}.
        </Typography>
      </LegalSection>
    </LegalPage>
  );
};

export default PrivacyNotice;
